import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Switch, Route, useLocation, useHistory } from "react-router-dom";
import { get } from "lodash";

import { getClientCredentialsApi, getMetaDataApi } from "../apis/portal";
import { MetaDataActions } from "../redux/reducers/MetaDataReducer";
import { ClientDataActions } from "../redux/reducers/ClientDataReducer";
import {
  ROUTE_APIDOCUMENT,
  ROUTE_APIS,
  ROUTE_APPLICATIONS,
  ROUTE_DASHBOARD,
  ROUTE_FAQS,
  ROUTE_CONNECTORLOGS,
  ROUTE_CUSTOMER_DOMAIN_MANAGEMENT,
  ROUTE_FEEDBACK,
  ROUTE_LISTAPI,
  ROUTE_LOGOUT,
  ROUTE_ROOT,
  ROUTE_SETTINGS,
  ROUTE_SIGNIN,
  ROUTE_SIGNUP,
  ROUTE_VERIFYOTP,
  ROUTE_EMAILVERIFICATION,
  ROUTE_SETPASSWORD,
  ROUTE_PROFILE,
  POONAWALLA_USER_ROLES_TYPES,
  ROUTE_LISTCATEGORIES,
  ROUTE_LISTPORTALUSERS,
  ROUTE_LISTPORTALUSERSAPPLICATION,
  ROUTE_ADMINLISTAPIS,
  ROUTE_SETUPAPI,
  ROUTE_LISTADMINUSERS,
  ROUTE_SETUPUSER,
  ROUTE_LISTREQUESTAPIS,
  ROUTE_LISTFEEDBACK,
  ROUTE_PRODUCT_CATALOGUE,
  OPEN_ROUTES,
  ROUTE_MFASIGNIN,
  ROUTE_FORGOTPASSWORD,
  ROUTE_API_CATALOGUE,
  ROUTE_LISTAPIACCESSREQUEST,
  ROUTE_SECRET_MANAGER,
  ROUTE_CERTIFICATE_MANAGER,
  ROUTE_LIST_WORKFLOW,
  ROUTE_SCHEDULED_JOB,
  ROUTE_SETUPSCHEDULED_JOB,
  ROUTE_WORKFLOWS_WORKSPACE,
  ROUTE_REPORTS,
  ROUTE_API_GATEWAY,
  ROUTE_SETUPAPI_GATEWAY,
} from "../globals/constants";
import { verifyToken } from "../globals/utils/jwtUtils";

import ProtectedRoute from "../components/ProtectedRoute";

/* LAZY LOADING PAGES */
const Main = React.lazy(() => import("../pages/main"));
const Reports = React.lazy(() => import("../pages/Reports"));
const Signin = React.lazy(() => import("../pages/auth/signin"));
const MFASignin = React.lazy(() => import("../pages/auth/mfaSignin"));
const Signup = React.lazy(() => import("../pages/auth/signup"));
const VerifyOtp = React.lazy(() => import("../pages/auth/verifyOtp"));
const emailVerification = React.lazy(() =>
  import("../pages/auth/emailVerification")
);
const ProductCatalogue = React.lazy(() =>
  import("../pages/auth/productCatalogue")
);
const APICatalogue = React.lazy(() => import("../pages/auth/apiCatalogue"));
const ForgotPassword = React.lazy(() => import("../pages/auth/forgotPassword"));
const SetPassword = React.lazy(() => import("../pages/auth/setPassword"));
const Logout = React.lazy(() => import("../pages/auth/logout"));

const Dashboard = React.lazy(() => import("../pages/dashboard"));
const Feedback = React.lazy(() => import("../pages/feedback"));
const Apis = React.lazy(() => import("../pages/apis"));
const Profile = React.lazy(() => import("../pages/profile"));
const Settings = React.lazy(() => import("../pages/settings"));
const ApiList = React.lazy(() => import("../pages/apiList"));
const Applications = React.lazy(() => import("../pages/applications"));
const FAQS = React.lazy(() => import("../pages/faqs"));
const ConnectorLogs = React.lazy(() => import("../pages/connectorLogs"));
const CustomerDomainManagement = React.lazy(() =>
  import("../pages/CustomerDomainManagement")
);
const ApiDocument = React.lazy(() => import("../pages/apiDocument"));
const CategoryList = React.lazy(() => import("../pages/categoryList"));
const PortalUsers = React.lazy(() => import("../pages/portalUsers"));
const AdminUsers = React.lazy(() => import("../pages/adminUsers"));
const AdminListApis = React.lazy(() => import("../pages/adminListApis"));
const AdminListWorkflow = React.lazy(() =>
  import("../pages/adminListWorkflow")
);
const AdminListSchduledJob = React.lazy(() =>
  import("../pages/adminListSchduledJob")
);

const AdminListSecretManager = React.lazy(() =>
  import("../pages/adminListSecretManager")
);
const AdminListCertificateManager = React.lazy(() =>
  import("../pages/adminListCertificateManager")
);

const AdminListAPIGateway = React.lazy(() =>
  import("../pages/adminListAPIGateway")
);

const AdminSetupApi = React.lazy(() => import("../pages/adminSetupApi"));
const AdminSetupUser = React.lazy(() => import("../pages/adminSetupUser"));
const Orchestrator = React.lazy(() => import("../modules/Orchestrator"));
const AdminSetupScheduledJob = React.lazy(() =>
  import("../pages/adminSetupScheduledJob")
);

const AdminSetupApiGateway = React.lazy(() =>
  import("../pages/adminSetupApiGateway")
);

const AdminListFeedback = React.lazy(() =>
  import("../pages/adminListFeedback")
);
const RequestApiList = React.lazy(() => import("../pages/requestApiList"));
const AdminListUsersApplication = React.lazy(() =>
  import("../pages/adminListUsersApplication")
);
const AdminListAPIAccessRequest = React.lazy(() =>
  import("../pages/AdminListAPIAccessRequest")
);

const Routes = () => {
  const { authData } = useSelector(
    (store) => ({
      authData: get(store, "auth.data", ""),
    }),
    shallowEqual
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const qp = new URLSearchParams(search);

  const getMetaData = async () => {
    try {
      const res = await getMetaDataApi();
      dispatch(MetaDataActions.SET_META_DATA(res.data));
    } catch (error) {
      console.log("SWW : ", error);
    }
  };

  const getClientCredentials = async () => {
    try {
      const { data } = await getClientCredentialsApi();
      dispatch(ClientDataActions.SET_CLIENT_DATA(data));
    } catch (error) {
      console.log("SWW : ", error);
    }
  };

  useEffect(() => {
    getMetaData();
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  useEffect(() => {
    // Only Load Data when user is logged in and user is a normal user.
    const idToken = get(authData, "idToken");
    if (
      idToken &&
      verifyToken(idToken) &&
      !POONAWALLA_USER_ROLES_TYPES.highPrivileged[get(authData, "user_type")]
    ) {
      getClientCredentials();
    }
  }, [authData]);

  useEffect(() => {
    if (!authData?.idToken && !OPEN_ROUTES.includes(pathname)) {
      qp.set("redirect_to", pathname);
      history.replace({
        search: qp.toString(),
      });
    } else if (
      authData?.idToken &&
      qp.get("redirect_to") &&
      pathname !== qp.get("redirect_to")
    ) {
      history.push({
        pathname: qp.get("redirect_to"),
        search: undefined,
      });
    }
  }, [authData?.idToken, pathname]);

  return (
    <Switch>
      {/* OPEN ROUTES */}
      <Route path={ROUTE_ROOT} exact component={Main} />
      {/* AUTH ROUTES */}
      <Route path={ROUTE_SIGNIN} exact component={Signin} />
      <Route path={ROUTE_MFASIGNIN} exact component={MFASignin} />
      <Route path={ROUTE_SIGNUP} exact component={Signup} />
      <Route path={ROUTE_VERIFYOTP} exact component={VerifyOtp} />
      <Route
        path={ROUTE_EMAILVERIFICATION}
        exact
        component={emailVerification}
      />
      <Route path={ROUTE_FORGOTPASSWORD} exact component={ForgotPassword} />
      <Route path={ROUTE_SETPASSWORD} exact component={SetPassword} />
      <Route path={ROUTE_LOGOUT} exact component={Logout} />
      <Route
        path={ROUTE_PRODUCT_CATALOGUE}
        exact
        component={ProductCatalogue}
      />
      <Route path={ROUTE_API_CATALOGUE} exact component={APICatalogue} />
      <Route path={ROUTE_FEEDBACK} exact component={Feedback} />
      <Route path={ROUTE_FAQS} exact component={FAQS} />
      <Route
        grants="ALL"
        path={ROUTE_CONNECTORLOGS}
        exact
        component={ConnectorLogs}
      />
      <ProtectedRoute
        grants={[POONAWALLA_USER_ROLES_TYPES.types.ADMIN]}
        path={ROUTE_REPORTS}
        exact
        component={Reports}
      />
      <ProtectedRoute
        grants="ALL"
        path={ROUTE_APPLICATIONS}
        exact
        component={Applications}
      />
      <ProtectedRoute
        grants="ALL"
        path={ROUTE_LISTAPI}
        exact
        component={ApiList}
      />
      <ProtectedRoute
        grants="ALL"
        path={ROUTE_DASHBOARD}
        exact
        component={Dashboard}
      />
      <ProtectedRoute grants="ALL" path={ROUTE_APIS} exact component={Apis} />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.CORPORATE,
          POONAWALLA_USER_ROLES_TYPES.types.INDIVIDUAL,
        ]}
        path={ROUTE_SETTINGS}
        exact
        component={Settings}
      />
      <ProtectedRoute
        grants="ALL"
        path={ROUTE_PROFILE}
        exact
        component={Profile}
      />
      <ProtectedRoute
        grants="ALL"
        path={ROUTE_APIDOCUMENT}
        exact
        component={ApiDocument}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_LISTCATEGORIES}
        exact
        component={CategoryList}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
          POONAWALLA_USER_ROLES_TYPES.types.APPROVER,
        ]}
        path={ROUTE_LISTPORTALUSERS}
        exact
        component={PortalUsers}
      />
      <ProtectedRoute
        grants={[POONAWALLA_USER_ROLES_TYPES.types.ADMIN]}
        path={ROUTE_LISTADMINUSERS}
        exact
        component={AdminUsers}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_LISTPORTALUSERSAPPLICATION}
        exact
        component={AdminListUsersApplication}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_ADMINLISTAPIS}
        exact
        component={AdminListApis}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_LIST_WORKFLOW}
        exact
        component={AdminListWorkflow}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_SCHEDULED_JOB}
        exact
        component={AdminListSchduledJob}
      />

      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_API_GATEWAY}
        exact
        component={AdminListAPIGateway}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_SECRET_MANAGER}
        exact
        component={AdminListSecretManager}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_CERTIFICATE_MANAGER}
        exact
        component={AdminListCertificateManager}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_SETUPAPI}
        exact
        component={AdminSetupApi}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_SETUPSCHEDULED_JOB}
        exact
        component={AdminSetupScheduledJob}
      />

      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_SETUPAPI_GATEWAY}
        exact
        component={AdminSetupApiGateway}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.APPROVER,
        ]}
        path={ROUTE_SETUPUSER}
        exact
        component={AdminSetupUser}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
        ]}
        path={ROUTE_WORKFLOWS_WORKSPACE}
        exact
        component={Orchestrator}
      />

      <ProtectedRoute
        grants={[POONAWALLA_USER_ROLES_TYPES.types.CHECKER]}
        path={ROUTE_LISTREQUESTAPIS}
        exact
        component={RequestApiList}
      />
      <ProtectedRoute
        grants={[POONAWALLA_USER_ROLES_TYPES.types.ADMIN]}
        path={ROUTE_LISTFEEDBACK}
        exact
        component={AdminListFeedback}
      />
      <ProtectedRoute
        grants={[
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.APPROVER,
        ]}
        path={ROUTE_LISTAPIACCESSREQUEST}
        exact
        component={AdminListAPIAccessRequest}
      />
      <ProtectedRoute
        grants={[POONAWALLA_USER_ROLES_TYPES.types.ADMIN]}
        path={ROUTE_CUSTOMER_DOMAIN_MANAGEMENT}
        exact
        component={CustomerDomainManagement}
      />
    </Switch>
  );
};

export default Routes;
